import type { Polygon } from 'geojson';
import type { FeatureProperties } from '../model';
import { evaluateAttributeRule } from '../../rules/evaluate';
import { lassoIntersectsGeometry } from '../../selection/lasso';
import { inspectGeoJson } from './geojsonInspection';
import type { ImportedVectorData } from './model';
import type { ImportedTableData } from './tabularImport';
import type { ImportMatchResult, ImportProgress, SelectiveImportSession } from './importPreflight';

export type ImportMethod = 'all' | 'attribute' | 'spatial';
export type PreparedImportItem = ImportedVectorData | ImportedTableData;
export const SUPPORTED_IMPORT_FORMATS =
  'CSV, Excel (.xlsx), GeoJSON, zipped Shapefile, GeoPackage and KML/KMZ';
export const IMPORT_METHODS = [
  {
    id: 'all',
    label: 'Import full dataset',
    description: 'Import every eligible feature or row.',
    help: 'Imports every eligible feature or row. A good choice when you need the complete dataset; very large files may take longer to display and work with.',
    icon: 'upload',
  },
  {
    id: 'attribute',
    label: 'Import by attribute',
    description: 'Choose records using attribute values.',
    help: 'Import only records matching the values you choose. Especially useful for large datasets: keeping only what you need can help the map stay fast and responsive.',
    icon: 'select-attribute',
  },
  {
    id: 'spatial',
    label: 'Import by spatial filter',
    description: 'Draw an area to import intersecting features.',
    help: 'Draw an area and import only features that intersect it. Ideal for focusing on one part of a large dataset while keeping the map lighter and easier to work with.',
    icon: 'select-rectangle',
  },
] as const;

export function itemCount(item: PreparedImportItem): number {
  return 'data' in item ? item.data.features.length : item.rows.length;
}

/** Cooperative batches keep cancellation responsive without coupling geometry work to React. */
export async function scanPreparedItems(
  items: readonly PreparedImportItem[],
  predicate: (properties: FeatureProperties, item: PreparedImportItem, index: number) => boolean,
  options: {
    readonly signal?: AbortSignal;
    readonly onProgress?: (progress: ImportProgress) => void;
  } = {},
): Promise<ImportMatchResult> {
  const indexes: number[] = [];
  const total = items.reduce((sum, item) => sum + itemCount(item), 0);
  let offset = 0;
  for (const item of items) {
    for (let index = 0; index < itemCount(item); index += 1) {
      if (offset % 512 === 0) {
        options.signal?.throwIfAborted();
        options.onProgress?.({ phase: 'finding-matches', completed: offset, total });
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
        options.signal?.throwIfAborted();
      }
      const properties =
        'data' in item
          ? item.data.features[index]!.properties
          : Object.fromEntries(
              item.fields.map((field, column) => [field.name, item.rows[index]![column] ?? null]),
            );
      if (predicate(properties, item, index)) indexes.push(offset);
      offset += 1;
    }
  }
  options.signal?.throwIfAborted();
  return {
    matchingRecordIndexes: Uint32Array.from(indexes),
    matchCount: indexes.length,
    totalFeatureCount: total,
    selectedGeometryBytes: 0,
  };
}

export function preparedSelectiveSession(
  items: readonly PreparedImportItem[],
): SelectiveImportSession {
  const first = items[0]!;
  const fields = [
    ...new Map(items.flatMap((item) => item.fields).map((field) => [field.name, field])).values(),
  ];
  return {
    preflight: {
      datasetName: 'data' in first ? first.suggestedLayerName : first.suggestedTableName,
      suggestedLayerName: 'data' in first ? first.suggestedLayerName : first.suggestedTableName,
      sourceFileName: first.provenance.originalFileName,
      sourceFormat:
        first.provenance.originalFormat === 'gpkg' ? 'geopackage' : first.provenance.originalFormat,
      featureCount: items.reduce((sum, item) => sum + itemCount(item), 0),
      geometryFamily: items.some((item) => 'data' in item) ? 'mixed' : 'none',
      crsStatus: 'declared',
      fields: fields.map((field) => ({
        ...field,
        displayType:
          field.type === 'integer' || field.type === 'number'
            ? 'Number'
            : field.type === 'boolean'
              ? 'Boolean'
              : field.type === 'date'
                ? 'Date'
                : 'Text',
        sampleValue: sampleValue(items, field.name),
      })),
      components: [],
      fullImportSafety: 'SAFE_FULL',
      selectiveImportAvailable: true,
    },
    findMatches: (filter, options) =>
      scanPreparedItems(
        items,
        (properties) =>
          evaluateAttributeRule(
            { kind: 'group', operator: 'and', children: filter.conditions },
            properties,
          ),
        options,
      ),
    planSubset: (matches, selectedFields) => ({
      ...matches,
      selectedFields,
      safety: items.some(
        (item) =>
          !('data' in item) && !item.fields.some((field) => selectedFields.includes(field.name)),
      )
        ? 'too-large'
        : 'safe',
      safetyMessage: 'Keep at least one attribute from every table.',
      selectedAttributeBytes: 0,
    }),
  };
}

function sampleValue(
  items: readonly PreparedImportItem[],
  name: string,
): string | number | boolean {
  for (const item of items) {
    const column = item.fields.findIndex((field) => field.name === name);
    if (column < 0) continue;
    for (let index = 0; index < Math.min(itemCount(item), 64); index += 1) {
      const value =
        'data' in item ? item.data.features[index]?.properties?.[name] : item.rows[index]?.[column];
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
        return typeof value === 'string' ? value.slice(0, 120) : value;
    }
  }
  return '—';
}

export function findSpatialMatches(
  items: readonly PreparedImportItem[],
  rectangle: Polygon,
  options?: Parameters<typeof scanPreparedItems>[2],
): Promise<ImportMatchResult> {
  return scanPreparedItems(
    items,
    (_properties, item, index) =>
      'data' in item &&
      !!item.data.features[index]?.geometry &&
      lassoIntersectsGeometry(rectangle, item.data.features[index]!.geometry!),
    options,
  );
}

/** Only selected canonical records are returned; neither source bytes nor predicates are saved. */
export function subsetPreparedItems(
  items: readonly PreparedImportItem[],
  matches: ImportMatchResult,
  selectedFields?: readonly string[],
): readonly PreparedImportItem[] {
  const selected = new Set(matches.matchingRecordIndexes);
  let offset = 0;
  return items.flatMap((item): PreparedImportItem[] => {
    const start = offset;
    offset += itemCount(item);
    if ('data' in item) {
      const features = item.data.features
        .filter((_feature, index) => selected.has(start + index))
        .map((feature) =>
          selectedFields
            ? {
                ...feature,
                properties: Object.fromEntries(
                  selectedFields
                    .filter((name) => feature.properties && Object.hasOwn(feature.properties, name))
                    .map((name) => [name, feature.properties![name]!]),
                ),
              }
            : feature,
        );
      if (!features.length) return [];
      const data = { type: 'FeatureCollection' as const, features };
      return [{ ...item, ...inspectGeoJson(data), data }];
    }
    const columns = item.fields.flatMap((field, index) =>
      !selectedFields || selectedFields.includes(field.name) ? [index] : [],
    );
    const rows = item.rows
      .filter((_row, index) => selected.has(start + index))
      .map((row) => columns.map((column) => row[column]!));
    if (rows.length && !columns.length)
      throw new Error('Keep at least one attribute from every table.');
    return rows.length
      ? [
          {
            ...item,
            fields: columns.map((column, outputColumn) => {
              const field = item.fields[column]!;
              const nullCount = rows.reduce(
                (sum, row) => sum + (row[outputColumn] === null ? 1 : 0),
                0,
              );
              return {
                ...field,
                presentCount: rows.length - nullCount,
                nullCount,
                nullable: nullCount > 0,
              };
            }),
            rows,
          },
        ]
      : [];
  });
}
